import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  FiUsers,
  FiSearch,
  FiMapPin,
  FiPhone,
  FiMail,
  FiSend,
  FiRefreshCw,
  FiCheckCircle,
  FiInfo,
  FiBriefcase,
  FiTrendingUp,
  FiExternalLink,
  FiMessageSquare,
} from "react-icons/fi";
import {
  getGroupedPartnerLeads,
  getCustomerDetailsById,
  sendPartnerLeadWhatsApp,
} from "../../../api/partner/partnerFeatureApis";

export default function PartnerLeadsHub() {
  const authState = useSelector((state) => state.auth);
  const token = authState?.user?.token || authState?.token || authState?.user?.refreshToken;
  const partnerUser = authState?.user?.userDetails || authState?.user || {};

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Lead Details Modal state
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadCustomers, setLeadCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [whatsappLoadingId, setWhatsappLoadingId] = useState(null);

  const [alertMsg, setAlertMsg] = useState(null);

  const fetchLeads = useCallback(
    async (page = 1) => {
      if (!token) return;
      setLoading(true);
      setAlertMsg(null);
      try {
        const data = await getGroupedPartnerLeads(token, {
          page_number: page,
          page_size: 15,
          order_by: "DESC",
          keyword_search: searchQuery,
        });

        const leadList = data?.groupedPartnerLeads || [];
        setLeads(leadList);
        setTotalPages(data?.totalPage || 1);
        setTotalRecords(data?.totalRecord || leadList.length);
        setCurrentPage(page);
      } catch (err) {
        console.error("Failed to fetch partner leads:", err);
        setAlertMsg({ type: "danger", text: "Failed to load partner leads. Please refresh." });
      } finally {
        setLoading(false);
      }
    },
    [token, searchQuery]
  );

  useEffect(() => {
    fetchLeads(1);
  }, [fetchLeads]);

  // Open Lead Details & Load Customer Locations
  const handleOpenDetails = async (lead) => {
    setSelectedLead(lead);
    setShowDetailsModal(true);
    setCustomersLoading(true);
    setLeadCustomers([]);

    try {
      const customerIds = lead.customer_ids || [];
      const customerPromises = customerIds.map((id) =>
        getCustomerDetailsById(token, id)
      );
      const resolvedCustomers = await Promise.all(customerPromises);
      setLeadCustomers(resolvedCustomers);
    } catch (err) {
      console.error("Error loading lead customer details:", err);
    } finally {
      setCustomersLoading(false);
    }
  };

  // WhatsApp Recommendation Dispatch
  const handleSendWhatsApp = async (customer) => {
    if (!customer.phone) {
      alert("Customer does not have a phone number registered.");
      return;
    }
    setWhatsappLoadingId(customer.id);
    try {
      const res = await sendPartnerLeadWhatsApp(token, {
        customerPhone: customer.phone,
        customerName: customer.name,
        leadName: selectedLead?.lead_name,
        area: selectedLead?.area,
        city: selectedLead?.city,
        state: selectedLead?.state,
        senderName: `${partnerUser.first_name || ""} ${partnerUser.last_name || ""}`.trim() || "Verified Partner",
        senderPhone: partnerUser.mobile_number || partnerUser.phone || "",
      });

      if (res?.statusType === "SUCCESS" || res?.status === "SUCCESS") {
        alert(`WhatsApp property recommendation sent successfully to ${customer.name}!`);
      } else {
        alert(res?.text || "Failed to send WhatsApp message.");
      }
    } catch (err) {
      alert(err?.response?.data?.text || "Failed to send WhatsApp message.");
    } finally {
      setWhatsappLoadingId(null);
    }
  };

  return (
    <div className="container-fluid px-3 px-md-4 py-3">
      {/* Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
        <div>
          <h3 className="fw-bold mb-1 d-flex align-items-center gap-2 text-dark">
            <FiUsers className="text-primary" /> Partner Grouped Leads
          </h3>
          <p className="text-muted mb-0 small">
            High-intent customer leads grouped across your properties, posts, and marketing campaigns.
          </p>
        </div>
        <button
          className="btn btn-outline-secondary d-flex align-items-center gap-1 btn-sm"
          onClick={() => fetchLeads(currentPage)}
          disabled={loading}
        >
          <FiRefreshCw className={loading ? "spin-icon" : ""} /> Refresh
        </button>
      </div>

      {alertMsg && (
        <div
          className={`alert alert-${alertMsg.type} alert-dismissible fade show d-flex align-items-center justify-content-between mb-3`}
          role="alert"
        >
          <span>{alertMsg.text}</span>
          <button
            type="button"
            className="btn-close"
            onClick={() => setAlertMsg(null)}
          />
        </div>
      )}

      {/* Metrics Banner */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-md-4">
          <div className="card border-0 shadow-sm p-3 rounded-3 bg-primary bg-opacity-10 border-start border-primary border-4">
            <span className="text-muted small fw-medium">Active Inquiries</span>
            <h4 className="fw-bold text-primary mb-0 mt-1">{totalRecords}</h4>
          </div>
        </div>
        <div className="col-12 col-md-4">
          <div className="card border-0 shadow-sm p-3 rounded-3 bg-success bg-opacity-10 border-start border-success border-4">
            <span className="text-muted small fw-medium">Verified Customer Base</span>
            <h4 className="fw-bold text-success mb-0 mt-1">
              {leads.reduce((acc, curr) => acc + (curr.customer_count || curr.customer_ids?.length || 0), 0)} Leads
            </h4>
          </div>
        </div>
        <div className="col-12 col-md-4">
          <div className="card border-0 shadow-sm p-3 rounded-3 bg-warning bg-opacity-10 border-start border-warning border-4">
            <span className="text-muted small fw-medium">Campaign Coverage</span>
            <h4 className="fw-bold text-warning mb-0 mt-1">Multi-Channel Active</h4>
          </div>
        </div>
      </div>

      {/* Search and Filters Bar */}
      <div className="card border-0 shadow-sm rounded-3 mb-4">
        <div className="card-body p-3">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              fetchLeads(1);
            }}
            className="row g-2 align-items-center"
          >
            <div className="col-12 col-md-8">
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-white border-end-0">
                  <FiSearch className="text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Search leads by property title, area, or city..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            <div className="col-12 col-md-4 d-flex gap-2">
              <button type="submit" className="btn btn-primary btn-sm px-3 shadow-sm">
                Search
              </button>
              {searchQuery && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => {
                    setSearchQuery("");
                    fetchLeads(1);
                  }}
                >
                  Clear
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Leads Table / Grid */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading partner leads...</span>
          </div>
          <p className="text-muted mt-2 small">Loading high-intent leads...</p>
        </div>
      ) : leads.length === 0 ? (
        <div className="card border-0 shadow-sm rounded-3 py-5 text-center">
          <div className="card-body">
            <FiUsers size={48} className="text-muted mb-3 opacity-50" />
            <h5 className="fw-semibold text-secondary">No Leads Found</h5>
            <p className="text-muted small mb-0">
              {searchQuery
                ? `No leads match "${searchQuery}".`
                : "No customer inquiries recorded yet. As customers interact with your properties and posts, leads will populate here automatically."}
            </p>
          </div>
        </div>
      ) : (
        <div className="row g-3">
          {leads.map((lead, idx) => {
            const customerCount = lead.customer_count || lead.customer_ids?.length || 0;
            const locationText = [lead.area, lead.city, lead.state].filter(Boolean).join(", ") || "Prime Location";

            return (
              <div key={idx} className="col-12 col-md-6 col-lg-4">
                <div className="card h-100 border-0 shadow-sm rounded-3 p-3 d-flex flex-column justify-content-between">
                  <div>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <span className="badge bg-primary bg-opacity-10 text-primary">
                        {customerCount} Interested {customerCount === 1 ? "Customer" : "Customers"}
                      </span>
                      {lead.property_id ? (
                        <span className="badge bg-light text-secondary border small">Property #{lead.property_id}</span>
                      ) : lead.post_id ? (
                        <span className="badge bg-light text-secondary border small">Post #{lead.post_id}</span>
                      ) : (
                        <span className="badge bg-light text-secondary border small">Promotion</span>
                      )}
                    </div>

                    <h5 className="fw-bold text-dark text-truncate mb-1" title={lead.lead_name}>
                      {lead.lead_name || "General Property Inquiry"}
                    </h5>

                    <p className="text-muted small mb-3">
                      <FiMapPin className="text-danger me-1" size={13} />
                      {locationText}
                    </p>
                  </div>

                  <div className="pt-3 border-top mt-2">
                    <button
                      className="btn btn-outline-primary btn-sm w-100 d-flex align-items-center justify-content-center gap-1 shadow-sm"
                      onClick={() => handleOpenDetails(lead)}
                    >
                      <FiInfo size={14} /> View Customers & Contact
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-4">
          <small className="text-muted">
            Page {currentPage} of {totalPages} ({totalRecords} Total Leads)
          </small>
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary btn-sm"
              disabled={currentPage <= 1 || loading}
              onClick={() => fetchLeads(currentPage - 1)}
            >
              Previous
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              disabled={currentPage >= totalPages || loading}
              onClick={() => fetchLeads(currentPage + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* =========================================================================
          LEAD DETAILS & CUSTOMER BREAKDOWN MODAL
          ========================================================================= */}
      {showDetailsModal && selectedLead && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
            <div className="modal-content border-0 shadow rounded-3">
              <div className="modal-header bg-light border-0 py-3">
                <div>
                  <h5 className="modal-title fw-bold text-dark mb-0">
                    {selectedLead.lead_name}
                  </h5>
                  <small className="text-muted">
                    <FiMapPin className="text-danger me-1" size={12} />
                    {[selectedLead.area, selectedLead.city, selectedLead.state].filter(Boolean).join(", ")}
                  </small>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDetailsModal(false)}
                />
              </div>

              <div className="modal-body p-4">
                <h6 className="fw-bold text-dark mb-3">
                  Interested Customers ({leadCustomers.length || selectedLead.customer_ids?.length || 0})
                </h6>

                {customersLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border spinner-border-sm text-primary" role="status" />
                    <p className="text-muted small mt-2">Loading customer profiles...</p>
                  </div>
                ) : leadCustomers.length === 0 ? (
                  <div className="text-center py-4 text-muted small">
                    No customer profiles available for this lead.
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {leadCustomers.map((cust) => {
                      const isSending = whatsappLoadingId === cust.id;

                      return (
                        <div
                          key={cust.id}
                          className="card border border-light shadow-sm rounded-3 p-3 bg-white"
                        >
                          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                            <div className="d-flex align-items-center gap-3">
                              {cust.photo ? (
                                <img
                                  src={cust.photo}
                                  alt={cust.name}
                                  className="rounded-circle object-fit-cover shadow-sm"
                                  style={{ width: "48px", height: "48px" }}
                                />
                              ) : (
                                <div
                                  className="rounded-circle bg-primary bg-opacity-10 text-primary fw-bold d-flex align-items-center justify-content-center shadow-sm"
                                  style={{ width: "48px", height: "48px", fontSize: "16px" }}
                                >
                                  {cust.name.charAt(0).toUpperCase()}
                                </div>
                              )}

                              <div>
                                <h6 className="fw-bold text-dark mb-0">{cust.name}</h6>
                                <div className="text-muted small">
                                  <FiMapPin size={12} className="text-danger me-1" />
                                  {cust.city ? `${cust.city}, ${cust.state || ""}` : "Verified Area"}
                                </div>
                                {cust.phone && (
                                  <div className="text-secondary small mt-1">
                                    <FiPhone size={12} className="me-1" /> {cust.phone}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="d-flex gap-2 w-100 w-md-auto">
                              {cust.phone && (
                                <a
                                  href={`tel:${cust.phone}`}
                                  className="btn btn-outline-success btn-sm d-flex align-items-center gap-1"
                                >
                                  <FiPhone size={13} /> Call
                                </a>
                              )}

                              <button
                                className="btn btn-success btn-sm d-flex align-items-center gap-1 px-3 shadow-sm"
                                onClick={() => handleSendWhatsApp(cust)}
                                disabled={isSending}
                              >
                                <FiSend size={13} />
                                {isSending ? "Sending..." : "Send WhatsApp"}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="modal-footer border-0 pt-0">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowDetailsModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
